import { useState } from 'react';
import type { PublicRoomView } from '../wire';
import type { StoredSession } from '../storage';
import { ShareRoom } from '../components/ShareRoom';
import { Loading } from '../components/Loading';
import type { ConnStatus } from '../useRoomSocket';
import { sound } from '../game/sound';
import { MAX_NAME_LEN, isValidRenameDraft } from '../rename';

export interface LobbyProps {
  session: StoredSession;
  lobby: PublicRoomView | null;
  connStatus: ConnStatus;
  onToggleReady: (ready: boolean) => void;
  onStartSolo: () => void;
  onLeave: () => void;
  /** PART 1: request a display-name change for THIS client's own seat only
   * (SeatCard below only ever wires this to the `isYou` card). */
  onRename: (name: string) => void;
  /** Optional "same device 2P" convenience hint -- rendered INSIDE this
   * screen's own centered flex column (see .lobby-screen justify-content)
   * so it reads as part of the lobby's content rhythm instead of a page-
   * level sibling stranded below a full-height flex box (the old App.tsx
   * placement -- see git history). */
  showLocal2pHint?: boolean;
  onStartLocalP2?: () => void;
  /** PART 6: opens the existing HelpModal from the lobby (first place a
   * new player actually waits before their first round). */
  onOpenHelp?: () => void;
}

export function Lobby({
  session,
  lobby,
  connStatus,
  onToggleReady,
  onStartSolo,
  onLeave,
  onRename,
  showLocal2pHint,
  onStartLocalP2,
  onOpenHelp,
}: LobbyProps): JSX.Element {
  if (!lobby) {
    return (
      <div className="screen lobby-screen" data-testid="screen-lobby">
        <Loading label={connStatus === 'reconnecting' ? 'Reconnecting...' : 'Connecting to room...'} />
        <button type="button" className="btn btn-link" onClick={onLeave} data-testid="btn-leave">
          Leave
        </button>
      </div>
    );
  }

  const mySeat = lobby.seats[session.seat - 1];
  const otherSeat = lobby.seats[session.seat === 1 ? 1 : 0];

  return (
    <div className="screen lobby-screen" data-testid="screen-lobby">
      <div className="screen-header">
        <button type="button" className="btn btn-link back-link" onClick={onLeave}>
          <span className="back-link-arrow" aria-hidden>
            ←
          </span>
          Leave
        </button>
        {onOpenHelp && (
          <button type="button" className="btn btn-link lobby-help-link" onClick={onOpenHelp} data-testid="btn-help">
            How to play
          </button>
        )}
      </div>
      <div className="screen-body">
        <h1>Lobby</h1>
        {/* PER-SCREEN 03/03b fix (ROUND 3, replaces DEFECT 8's fix): Share/
            Copy-link is now a STABLE secondary in every lobby state -- see
            ShareRoom.tsx's own comment. "I'm ready" (below) is the single
            primary action throughout, instead of the two of them trading
            off the yellow fill (and briefly BOTH holding it on 03: seat
            open + "I'm ready" pre-check). */}
        <ShareRoom code={lobby.code} />

        <div className="seat-list">
          <SeatCard seatLabel="P1" seat={lobby.seats[0]} isYou={session.seat === 1} onRename={session.seat === 1 ? onRename : undefined} />
          <SeatCard seatLabel="P2" seat={lobby.seats[1]} isYou={session.seat === 2} onRename={session.seat === 2 ? onRename : undefined} />
        </div>

        <div className="lobby-actions">
          {/* DEFECT fix (vision gen-4): the comment above ("once both seats
              are occupied, I'm ready is the actual primary action") demoted
              ShareRoom to secondary but never actually PROMOTED this control
              in the not-yet-ready state -- it only picked up the app's
              primary yellow fill (`--on`) once already CHECKED. Between
              those two states (both seats filled, not yet ready -- exactly
              the moment "I'm ready" IS the goal action) neither control
              carried the primary treatment: zero yellow-fill pixels, no
              visible CTA at all. `--goal` reuses the SAME `--on` yellow fill
              (see index.css) rather than inventing a second treatment, and
              only applies pre-check (once checked, `--on` already covers
              it) so emphasis follows the goal action instead of vanishing
              partway through the lobby flow. */}
          {/* PER-SCREEN 03 fix: "I'm ready" is the screen's one real goal
              action at every point in the lobby flow -- whether or not P2
              has joined yet -- so it now always carries the primary
              (`--goal`) treatment pre-check, not just once both seats are
              filled. "Start solo round" demotes to a quiet text link below
              (was a second outline button competing for the same visual
              weight) so there is exactly one primary CTA on screen. */}
          <label className={`ready-toggle${mySeat?.ready ? ' ready-toggle--on' : ' ready-toggle--goal'}`}>
            <input
              type="checkbox"
              checked={mySeat?.ready ?? false}
              onChange={(e) => {
                // User gesture -- unlock the AudioContext here (SPEC: a
                // click that already happens before gameplay), so sounds
                // aren't silently blocked once the round starts.
                sound.resume();
                onToggleReady(e.target.checked);
              }}
              data-testid="ready-checkbox"
            />
            <span>{mySeat?.ready ? '✓ Ready, waiting for the round' : "I'm ready"}</span>
          </label>

          {(!otherSeat?.name || !otherSeat?.connected) && (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => {
                sound.resume();
                onStartSolo();
              }}
              data-testid="btn-start-solo"
            >
              Start solo round
            </button>
          )}
        </div>

        {!otherSeat?.name && (
          <p className="dim">
            Waiting for a second player. Share the code above, or start solo. P2's seat can be filled mid-game.
          </p>
        )}
        {otherSeat?.name && !otherSeat.connected && (
          <p className="dim">
            {otherSeat.name} disconnected. Start solo, or wait for them to reconnect.
          </p>
        )}
        {otherSeat?.name && otherSeat.connected && !otherSeat.ready && (
          <p className="dim">Waiting for {otherSeat.name} to ready up...</p>
        )}

        {showLocal2pHint && onStartLocalP2 && (
          <button
            type="button"
            className="btn btn-link local2p-hint"
            onClick={() => {
              sound.resume();
              onStartLocalP2();
            }}
          >
            Play same device 2P (WASD + Arrows)
          </button>
        )}
      </div>
    </div>
  );
}

function SeatCard({
  seatLabel,
  seat,
  isYou,
  onRename,
}: {
  seatLabel: string;
  seat: PublicRoomView['seats'][number];
  isYou: boolean;
  /** PART 1: undefined for the OTHER player's seat -- SeatCard never shows
   * an edit affordance without this, so the other seat can never be
   * renamed from here. */
  onRename?: (name: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(seat.name ?? '');

  const trimmed = draft.trim();
  // PART 1: mirrors server/src/util/validate.ts validateName's length gate
  // (1-32 VISIBLE chars, checked against the TRIMMED string) so the common
  // case never round-trips a rejection. The server remains the sole
  // authority (profanity screen, final trim) -- this only pre-empts the
  // obvious client-detectable cases.
  const canSubmit = isValidRenameDraft(draft);

  function startEdit(): void {
    setDraft(seat.name ?? '');
    setEditing(true);
  }

  function cancelEdit(): void {
    setEditing(false);
  }

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit || !onRename) return;
    onRename(trimmed);
    // Close back to display mode immediately -- the card's own `seat.name`
    // still reads whatever the server LAST broadcast (old name, until the
    // authoritative lobby update arrives) rather than the just-typed draft:
    // never optimistically rewrite the displayed name (SPEC.md §1.1). A
    // rejection surfaces via the existing ErrorBanner (App.tsx), not a
    // second error UI here.
    setEditing(false);
  }

  const showEditControl = isYou && !!onRename && !!seat.name;
  const isEmpty = !seat.name;
  // TOP-10 #2/#7: a left accent bar (index.css `.seat-card::before`) in the
  // seat's own P1-yellow/P2-green identity color -- decorative, does not
  // touch any AA-locked text token.
  const accentVar = { '--seat-accent': seatLabel === 'P1' ? 'var(--p1)' : 'var(--p2)' } as React.CSSProperties;

  return (
    <div
      className={`seat-card${seat.ready ? ' seat-ready' : ''}${isEmpty ? ' seat-card--empty' : ''}`}
      style={accentVar}
      data-testid={`seat-${seatLabel.toLowerCase()}`}
    >
      <div className="seat-card-label">
        {seatLabel} {isYou && <span className="seat-you-badge">(you)</span>}
      </div>
      {showEditControl && editing ? (
        <form className="seat-rename-form" onSubmit={submit} data-testid="rename-form">
          <input
            type="text"
            className="seat-rename-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={MAX_NAME_LEN}
            autoFocus
            aria-label="Edit your name"
            data-testid="rename-input"
          />
          <div className="seat-rename-actions">
            <button
              type="submit"
              className="btn btn-primary btn-small"
              disabled={!canSubmit}
              data-testid="rename-submit"
            >
              Save
            </button>
            <button type="button" className="btn btn-link btn-small" onClick={cancelEdit} data-testid="rename-cancel">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="seat-card-name-row">
          {/* TOP-10 #7 fix: an empty seat previously showed "Waiting for
              player..." in the SAME bold-white weight as a real occupied
              name -- reading as filled at a glance. A muted italic ghost
              avatar + a quiet pulse now visually demote it to a genuine
              placeholder state (see index.css `.seat-avatar-ghost`, gated
              under `prefers-reduced-motion`/Settings same as every other
              animation in this app). */}
          {isEmpty && <span className="seat-avatar-ghost" aria-hidden="true" />}
          <div className="seat-card-name">{seat.name ?? 'Waiting for player...'}</div>
          {showEditControl && (
            <button
              type="button"
              className="btn btn-link seat-edit-btn"
              onClick={startEdit}
              aria-label="Edit name"
              data-testid="btn-edit-name"
            >
              Edit name
            </button>
          )}
        </div>
      )}
      <div className="seat-card-status">
        {seat.name ? (
          <>
            <span className={`status-dot ${seat.connected ? 'status-online' : 'status-offline'}`} aria-hidden />
            {seat.connected ? 'Connected' : 'Disconnected'} · {seat.ready ? 'Ready' : 'Not ready'}
          </>
        ) : (
          'Open seat'
        )}
      </div>
    </div>
  );
}
